import { ref, type Ref } from 'vue'

interface UseCarouselSwipeOptions {
  onSwipeLeft: () => void
  onSwipeRight: () => void
  onInteraction: () => void
  swipeThreshold?: number
}

const DIRECTION_LOCK_DISTANCE = 10

export function useCarouselSwipe(
  viewportRef: Ref<HTMLElement | null>,
  options: UseCarouselSwipeOptions,
) {
  const dragOffset = ref(0)
  const threshold = options.swipeThreshold ?? 50

  let isDragging = false
  let directionLocked = false
  let startX = 0
  let startY = 0
  let pointerId: number | null = null

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return
    isDragging = true
    directionLocked = false
    startX = e.clientX
    startY = e.clientY
    pointerId = e.pointerId
    dragOffset.value = 0
    // Pointer capture is deferred to onPointerMove once horizontal swipe is
    // confirmed.  Capturing here would redirect synthesised mouse events
    // (mousedown/mouseup/click) to the viewport, preventing the editor's
    // click handler from selecting the component the user actually clicked.
  }

  function onPointerMove(e: PointerEvent) {
    if (!isDragging) return

    const dx = e.clientX - startX
    const dy = e.clientY - startY

    if (!directionLocked) {
      const absDx = Math.abs(dx)
      const absDy = Math.abs(dy)
      if (absDx < DIRECTION_LOCK_DISTANCE && absDy < DIRECTION_LOCK_DISTANCE) return

      if (absDy > absDx) {
        // Vertical scroll intent — bail
        isDragging = false
        return
      }
      directionLocked = true
      // Now that we know it's a horizontal swipe, capture the pointer so
      // move/up events keep arriving even if the cursor leaves the viewport.
      if (pointerId !== null) {
        viewportRef.value?.setPointerCapture(pointerId)
      }
    }

    e.preventDefault()
    dragOffset.value = dx
  }

  function onPointerUp() {
    if (!isDragging) return
    isDragging = false

    if (Math.abs(dragOffset.value) > threshold) {
      if (dragOffset.value > 0) {
        options.onSwipeRight()
      } else {
        options.onSwipeLeft()
      }
      options.onInteraction()
    }

    dragOffset.value = 0
    pointerId = null
  }

  return { dragOffset, onPointerDown, onPointerMove, onPointerUp }
}
